import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, CheckCircle2, XCircle, Clock, Crown, Settings } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  liveCapabilities,
  limitations,
  planned,
  enterprise,
} from '@/agent/capabilities';

interface Props {
  /** Optional custom trigger; defaults to a discreet ghost button with HelpCircle. */
  trigger?: React.ReactNode;
}

/**
 * Side sheet that documents the agent's current capabilities, limitations,
 * roadmap and Enterprise-only features. Deliberately sober — no AI-magic
 * gradients, just clear typography and grouped lists.
 */
export function AgentCapabilitiesPanel({ trigger }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs">
            <HelpCircle className="h-3.5 w-3.5" />
            ¿Qué puedo preguntarte?
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="text-base">Capacidades del Agente EXPAI</SheetTitle>
          <SheetDescription className="text-xs">
            Lo que el agente puede hacer hoy en tu plataforma MES.
          </SheetDescription>
          <div className="pt-2">
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs w-full sm:w-auto" asChild onClick={() => setOpen(false)}>
              <Link to="/agent/settings">
                <Settings className="h-3.5 w-3.5" /> Configuración del agente
              </Link>
            </Button>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-5rem)]">
          <div className="px-5 py-4 space-y-6">
            {/* LIVE */}
            <section>
              <SectionHeader
                icon={<CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />}
                title="Capacidades actuales"
                subtitle="Funcionalidades operativas hoy."
              />
              <div className="space-y-4">
                {liveCapabilities.map((group) => (
                  <div key={group.module}>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                      {group.module}
                    </div>
                    <ul className="space-y-2">
                      {group.items.map((item) => (
                        <li key={item.title} className="rounded-md border bg-card p-2.5">
                          <div className="text-xs font-medium">{item.title}</div>
                          <div className="text-[11px] text-muted-foreground italic mt-0.5">
                            “{item.example}”
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <Separator />

            {/* LIMITATIONS */}
            <section>
              <SectionHeader
                icon={<XCircle className="h-4 w-4 text-destructive" />}
                title="Lo que NO hago"
                subtitle="Límites explícitos por diseño."
              />
              <ul className="space-y-1.5">
                {limitations.map((l) => (
                  <li key={l} className="flex gap-2 text-xs">
                    <span className="text-muted-foreground mt-0.5">·</span>
                    <span className="text-foreground/90">{l}</span>
                  </li>
                ))}
              </ul>
            </section>

            <Separator />

            {/* PLANNED */}
            <section>
              <SectionHeader
                icon={<Clock className="h-4 w-4 text-[hsl(var(--warning))]" />}
                title="Próximamente"
                subtitle="En el roadmap de los próximos sprints."
              />
              <ul className="space-y-1.5">
                {planned.map((p) => (
                  <li key={p} className="flex gap-2 text-xs">
                    <span className="text-muted-foreground mt-0.5">·</span>
                    <span className="text-foreground/90">{p}</span>
                  </li>
                ))}
              </ul>
            </section>

            <Separator />

            {/* ENTERPRISE */}
            <section>
              <SectionHeader
                icon={<Crown className="h-4 w-4 text-primary" />}
                title="Planes Enterprise"
                subtitle="Disponible bajo contrato."
                badge="Enterprise"
              />
              <ul className="space-y-1.5">
                {enterprise.map((e) => (
                  <li key={e} className="flex gap-2 text-xs">
                    <span className="text-muted-foreground mt-0.5">·</span>
                    <span className="text-foreground/90">{e}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function SectionHeader({
  icon, title, subtitle, badge,
}: { icon: React.ReactNode; title: string; subtitle?: string; badge?: string }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
        {badge && <Badge variant="outline" className="text-[9px] h-4 px-1.5">{badge}</Badge>}
      </div>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}
